import { useMemo } from 'react';
import { cn } from '@anytag/shared-utils';
import { ComboboxOption } from '@anyui/core';
import { SelectOption } from '@anytag/shared-types';
import { HoverCardTrigger } from '@radix-ui/react-hover-card';
import { Icon } from '../../Icon';
import { CheckBox } from '../../CheckBox';
import { TextCutter } from '../../TextCutter';
import { useSelectContext } from '../select-context';
import { getOptionLabel, getOptionValue } from '../utils';

interface SelectSubTriggerProps {
  active?: boolean;
  indeterminate?: boolean; // For multiple select
  option: SelectOption<string>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export const SelectSubTrigger = ({ option, active, onClick, indeterminate }: SelectSubTriggerProps) => {
  const {
    setHoveringOption,
    selectProps: { multiple }
  } = useSelectContext();

  // value for searching
  const keywords = useMemo(
    () => [getOptionLabel(option), ...(option.submenu?.map(getOptionLabel) || [])].filter((l) => typeof l === 'string'),
    [option]
  );

  return (
    <ComboboxOption
      keywords={keywords}
      value={getOptionValue(option)}
      disabled // Prevent default onSelect
      unstyled
      asChild
    >
      <HoverCardTrigger asChild>
        <div
          onClick={onClick}
          onMouseOut={() => setHoveringOption(null)}
          onMouseOver={() => setHoveringOption(getOptionValue(option))}
          className={cn(
            // Custom matching with anyui Combobox component
            'col-span-2 grid cursor-default grid-cols-subgrid items-baseline rounded-sm py-1 pr-3 pl-2 text-sm/6 text-zinc-900 outline-hidden',
            active ? 'bg-zinc-100' : 'hover:bg-zinc-50'
          )}
        >
          <div className="col-start-2 flex items-center gap-2">
            {multiple ? <CheckBox checked={active} indeterminate={indeterminate} /> : option.prefixIcon}
            <div className="flex flex-1 items-center justify-between gap-2">
              <TextCutter text={getOptionLabel(option)} lines={1} />
              <Icon icon="caret-right" size="8px" className="text-zinc-500" />
            </div>
          </div>
        </div>
      </HoverCardTrigger>
    </ComboboxOption>
  );
};
